import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api, ApiError } from '../lib/api'
import { useAuth } from '../lib/auth'
import { humanError } from '../lib/errors'
import { itemStatusLabel } from '../lib/labels'

type Item = {
  id: number
  title: string
  url: string
  price: number | null
  note: string
  status: 'available' | 'reserved'
  reservedBy: number | null
  reservedByName: string | null
}

type Pledge = {
  id: number
  userId: number
  userName?: string
  amount: number
  note: string
}

export function SharePage() {
  const { token } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const authHref = `/auth?next=${encodeURIComponent(`/share/${token ?? ''}`)}`
  const [meta, setMeta] = useState<{ wishlistId: number; title: string; ownerName?: string } | null>(
    null,
  )
  const [items, setItems] = useState<Item[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedItem, setSelectedItem] = useState<number | null>(null)
  const [pledges, setPledges] = useState<Pledge[]>([])
  const [pledgeTotal, setPledgeTotal] = useState(0)
  const [pledgesHidden, setPledgesHidden] = useState(false)

  function requireLogin() {
    navigate(authHref)
  }

  async function reloadItems(wishlistId: number) {
    const data = await api<{ items: Item[] }>(
      `/api/items/by-wishlist/${wishlistId}?token=${token}`,
    )
    setItems(data.items)
  }

  useEffect(() => {
    async function load() {
      setLoading(true)
      setError('')
      try {
        const share = await api<{ wishlistId: number; title: string; ownerName?: string }>(
          `/api/shares/token/${token}`,
        )
        setMeta(share)
        await reloadItems(share.wishlistId)
      } catch (err) {
        setError(err instanceof ApiError ? humanError(err.message) : 'Не удалось открыть ссылку')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [token])

  async function reserve(item: Item) {
    if (!user) {
      requireLogin()
      return
    }
    if (item.status === 'reserved' && item.reservedBy !== user.id) {
      setError(humanError('already_reserved'))
      return
    }
    try {
      const path =
        item.status === 'reserved' && item.reservedBy === user.id
          ? `/api/items/${item.id}/unreserve?token=${token}`
          : `/api/items/${item.id}/reserve?token=${token}`
      await api(path, { method: 'POST' })
      if (meta) await reloadItems(meta.wishlistId)
    } catch (err) {
      setError(err instanceof ApiError ? humanError(err.message) : 'Ошибка брони')
    }
  }

  async function openPledges(itemId: number) {
    if (!user) {
      requireLogin()
      return
    }
    setSelectedItem(itemId)
    try {
      const data = await api<{ pledges: Pledge[]; total: number; hidden?: boolean }>(
        `/api/pledges/by-item/${itemId}?token=${token}`,
      )
      setPledgesHidden(Boolean(data.hidden))
      setPledges(data.pledges)
      setPledgeTotal(data.total)
    } catch (err) {
      setError(err instanceof ApiError ? humanError(err.message) : 'Не удалось загрузить взносы')
    }
  }

  async function savePledge(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!user) {
      requireLogin()
      return
    }
    if (!selectedItem) return
    const form = new FormData(e.currentTarget)
    try {
      await api(`/api/pledges/by-item/${selectedItem}?token=${token}`, {
        method: 'PUT',
        body: JSON.stringify({
          amount: Number(form.get('amount')),
          note: String(form.get('note') || ''),
        }),
      })
      await openPledges(selectedItem)
    } catch (err) {
      setError(err instanceof ApiError ? humanError(err.message) : 'Не удалось сохранить взнос')
    }
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand">
          <div className="brand-mark">
            W<span>ish</span>
          </div>
          <div className="brand-sub">общий доступ · гость</div>
        </div>
        <div className="nav-actions">
          {user ? (
            <Link className="btn" to="/app">
              В приложение
            </Link>
          ) : (
            <Link className="btn btn-primary" to={authHref}>
              Войти
            </Link>
          )}
        </div>
      </header>

      <section className="panel stack">
        {loading ? (
          <div className="loading-block" role="status">
            <span className="spinner" aria-hidden="true" />
            Открываем список…
          </div>
        ) : (
          <>
            <div>
              <h1 className="section-title">{meta?.title || 'Share'}</h1>
              <p className="section-sub">
                {meta?.ownerName ? `Список от ${meta.ownerName}` : 'Публичная ссылка на вишлист'}
              </p>
            </div>

            {error && (
              <div className="alert alert-error" role="alert">
                {error}
              </div>
            )}

            {!user && (
              <div className="alert" style={{ background: 'var(--accent-soft)', border: '1px solid rgba(15,110,86,0.18)' }}>
                Войдите, чтобы бронировать подарки и оставлять взносы.
              </div>
            )}

            <div className="list">
              {items.length === 0 && !error && (
                <div className="empty-state">
                  <strong>Список пуст</strong>
                  Владелец ещё не добавил позиции.
                </div>
              )}
              {items.map((item) => (
                <article key={item.id} className="item-card">
                  <div className="item-card-head">
                    <strong className="item-card-title">{item.title}</strong>
                    {item.url && (
                      <a className="item-card-link" href={item.url} target="_blank" rel="noreferrer">
                        {item.url.replace(/^https?:\/\//, '').slice(0, 48)}
                        {item.url.length > 56 ? '…' : ''}
                      </a>
                    )}
                    {item.note && <p className="item-card-note">{item.note}</p>}
                    <div className="item-card-meta">
                      {item.price != null && <span className="price">{item.price} ₽</span>}
                      <span className={`badge ${item.status === 'reserved' ? 'warn' : 'accent'}`}>
                        {itemStatusLabel(item.status, {
                          isOwner: false,
                          reservedByName: item.reservedByName,
                        })}
                      </span>
                    </div>
                  </div>
                  <div className="item-actions">
                    {item.status === 'available' && (
                      <button className="btn btn-primary" type="button" onClick={() => void reserve(item)}>
                        Беру в подарок
                      </button>
                    )}
                    {item.status === 'reserved' && item.reservedBy === user?.id && (
                      <button className="btn" type="button" onClick={() => void reserve(item)}>
                        Снять резерв
                      </button>
                    )}
                    <button className="btn btn-ghost" type="button" onClick={() => void openPledges(item.id)}>
                      Взносы
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}

        {user && selectedItem != null && (
          <div className="panel stack subpanel">
            <h2 className="section-title">Взносы · итого {pledgeTotal} ₽</h2>
            {pledgesHidden ? (
              <p className="muted">Детали взносов скрыты для владельца списка.</p>
            ) : (
              <>
                {pledges.length === 0 ? (
                  <p className="muted">Пока никто не скинулся.</p>
                ) : (
                  <div className="list">
                    {pledges.map((p) => (
                      <div key={p.id} className="list-item">
                        <div>
                          <strong>{p.userName || `user#${p.userId}`}</strong>
                          <div className="muted">{p.note || '—'}</div>
                        </div>
                        <span className="price">{p.amount} ₽</span>
                      </div>
                    ))}
                  </div>
                )}
                <form className="stack" onSubmit={(e) => void savePledge(e)}>
                  <label>
                    Сколько готов скинуть
                    <input name="amount" type="number" min={0} step="0.01" required />
                  </label>
                  <label>
                    Комментарий
                    <input name="note" placeholder="могу до пятницы" />
                  </label>
                  <button className="btn btn-primary" type="submit">
                    Сохранить взнос
                  </button>
                </form>
              </>
            )}
          </div>
        )}
      </section>
    </div>
  )
}
